import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useId, useState } from 'react'
import { useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { toast } from 'sonner'
import { z } from 'zod'
import { ErrorList } from './forms.tsx'
import { Button } from './ui/button.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from './ui/dialog.tsx'
import { Icon } from './ui/icon.tsx'
import { Input } from './ui/input.tsx'

const SendDetailsSchema = z.object({
	email: z.string().email('Voer een geldig e-mailadres in'),
	exhibitionId: z.string(),
})

export function SendEventDetailsDialog({
	isOpen,
	onClose,
	exhibition,
}: {
	isOpen: boolean
	onClose: () => void
	exhibition: {
		id: string
		title: string
		type: string
	}
}) {
	const fetcher = useFetcher()
	const formId = useId()
	const [emailSent, setEmailSent] = useState(false)

	const [form, fields] = useForm({
		id: formId,
		constraint: getZodConstraint(SendDetailsSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: SendDetailsSchema })
		},
		shouldValidate: 'onBlur',
		shouldRevalidate: 'onInput',
	})

	const isSubmitting = fetcher.state !== 'idle'

	// Handle successful submission
	if (fetcher.data?.success && !emailSent) {
		setEmailSent(true)
		toast.success('De gegevens zijn verstuurd naar je e-mailadres!')
		setTimeout(() => {
			onClose()
			setEmailSent(false)
		}, 2000)
	}

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						Ontvang details van{' '}
						{exhibition.type === 'gallery' ? 'galerie' : 'expositie'}
					</DialogTitle>
					<DialogDescription>
						We sturen je alle informatie over "{exhibition.title}" naar je
						e-mailadres.
					</DialogDescription>
				</DialogHeader>

				{emailSent ? (
					<div className="py-8 text-center">
						<Icon
							name="check"
							className="mx-auto mb-4 h-16 w-16 text-green-600"
						/>
						<p className="font-medium text-green-800">E-mail verstuurd!</p>
						<p className="mt-2 text-sm text-gray-600">
							Check je inbox voor alle details.
						</p>
					</div>
				) : (
					<fetcher.Form
						method="POST"
						action="/resources/send-event-details"
						{...getFormProps(form)}
						className="space-y-4 pt-4"
					>
						<HoneypotInputs />
						<input type="hidden" name="exhibitionId" value={exhibition.id} />

						<div>
							<Input
								{...getInputProps(fields.email, { type: 'email' })}
								placeholder="jouw@email.nl"
								disabled={isSubmitting}
								className="w-full"
								autoComplete="email"
							/>
							<ErrorList
								errors={fields.email.errors}
								id={fields.email.errorId}
							/>
						</div>

						<div className="flex justify-end gap-3">
							<Button
								type="button"
								variant="ghost"
								onClick={onClose}
								disabled={isSubmitting}
							>
								Annuleren
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? (
									<>
										<Icon name="update" className="mr-2 animate-spin" />
										Versturen...
									</>
								) : (
									<>
										<Icon name="envelope-closed" className="mr-2" />
										Stuur mij de gegevens
									</>
								)}
							</Button>
						</div>
					</fetcher.Form>
				)}
			</DialogContent>
		</Dialog>
	)
}
